import React from 'react';
import styled from 'styled-components';

interface TreeLayoutProps {
  children: React.ReactNode;
  className?: string;
}

interface TreeNodeProps {
  children: React.ReactNode;
  level?: number;
  className?: string;
}

interface TreeItemProps {
  children: React.ReactNode;
  isSelected?: boolean;
  count?: number;
  onClick?: () => void;
  className?: string;
}

const TreeContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding-right: 16px;
`;

const TreeNode = styled.div<{ $level: number }>`
  padding-left: ${({ $level }) => $level * 16}px;
  margin-bottom: 2px;
`;

const TreeItem = styled.div<{ $count?: number; $isSelected: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  user-select: none;
  opacity: ${({ $count }) => ($count === 0 ? 0.5 : 1)};
  color: ${({ $count, theme }) => ($count === 0 ? theme.colors.textSecondary : 'inherit')};
  background-color: ${({ $isSelected, theme }) => ($isSelected ? theme.colors.palette.aquas[0] : 'transparent')};
  font-weight: ${({ $isSelected }) => ($isSelected ? '600' : 'normal')};

  &:hover {
    opacity: 1;
    color: inherit;
    background-color: ${({ $isSelected, theme }) => ($isSelected ? theme.colors.palette.aquas[0] : theme.colors.palette.grayscale[1])};
  }
`;

export const TreeLayout: React.FC<TreeLayoutProps> = ({ children, className }) => (
  <TreeContainer className={className}>
    {children}
  </TreeContainer>
);

export const TreeLayoutNode: React.FC<TreeNodeProps> = ({ children, level = 0, className }) => (
  <TreeNode $level={level} className={className}>
    {children}
  </TreeNode>
);

export const TreeLayoutItem: React.FC<TreeItemProps> = ({ 
  children, 
  isSelected = false, 
  count, 
  onClick,
  className 
}) => (
  <TreeItem 
    $isSelected={isSelected} 
    $count={count} 
    onClick={onClick}
    className={className}
  >
    {children}
  </TreeItem>
);

TreeLayout.displayName = 'TreeLayout';
TreeLayoutNode.displayName = 'TreeLayoutNode';
TreeLayoutItem.displayName = 'TreeLayoutItem';